import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import keycloak from "@/lib/keycloak";

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL;
const KEYCLOAK_ENABLED =
  !!KEYCLOAK_URL &&
  !KEYCLOAK_URL.includes(".invalid") &&
  !KEYCLOAK_URL.includes("placeholder");

interface AuthUser {
  name?: string;
  email?: string;
  sub?: string;
}

interface AuthContextType {
  initialized: boolean;
  authenticated: boolean;
  user: AuthUser | null;
  roles: string[];
  token: string | undefined;
  login: () => void;
  logout: () => void;
}

function rolesFromToken(): string[] {
  return keycloak.tokenParsed?.realm_access?.roles ?? [];
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [token, setToken] = useState<string | undefined>(undefined);
  const initCalled = useRef(false);

  useEffect(() => {
    if (initCalled.current) return;
    initCalled.current = true;

    if (!KEYCLOAK_ENABLED) {
      // Keycloak isn't deployed yet for this environment; skip init so the
      // app loads without redirecting to a non-existent SSO endpoint.
      setInitialized(true);
      return;
    }

    keycloak
      .init({
        onLoad: "check-sso",
        checkLoginIframe: false,
      })
      .then((auth) => {
        setAuthenticated(auth);
        if (auth && keycloak.tokenParsed) {
          setUser({
            name: keycloak.tokenParsed.name as string | undefined,
            email: keycloak.tokenParsed.email as string | undefined,
            sub: keycloak.tokenParsed.sub,
          });
          setRoles(rolesFromToken());
          setToken(keycloak.token);
        }
        setInitialized(true);
      })
      .catch(() => {
        setInitialized(true);
        setAuthenticated(false);
      });

    const clearSession = () => {
      setAuthenticated(false);
      setUser(null);
      setRoles([]);
      setToken(undefined);
    };

    keycloak.onTokenExpired = () => {
      keycloak.updateToken(60).catch(clearSession);
    };

    keycloak.onAuthRefreshError = clearSession;

    const refreshInterval = setInterval(() => {
      if (keycloak.authenticated) {
        keycloak
          .updateToken(60)
          .then((refreshed) => {
            if (refreshed) {
              setRoles(rolesFromToken());
              setToken(keycloak.token);
            }
          })
          .catch(clearSession);
      }
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, []);

  const login = () => {
    if (!KEYCLOAK_ENABLED) return;
    keycloak.login({
      redirectUri: window.location.origin + "/dashboard",
    });
  };
  const logout = () => {
    if (!KEYCLOAK_ENABLED) return;
    keycloak.logout({ redirectUri: window.location.origin });
  };

  return (
    <AuthContext.Provider
      value={{ initialized, authenticated, user, roles, token, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
